import {
  camelCaseObjectKeys,
  snakeCaseObjectKeys,
} from '@doordash/object-utils'
import axios from 'axios'
import { isEmpty } from 'lodash'
import { bffOpts } from './config'

// ---------------------* POST: Submit ABN *---------------------

export interface SubmitAbnFields {
  australianBusinessNumber: string
  isGstRegistered: boolean
}

/**
 * Submits an ABN request to update AUS Dasher applicant's tax info
 * @param {SubmitAbnFields} req request with australian business number
 * @returns A promise with ABN data
 *
 * Error codes:
 * 400: Bad request (req format errors or ABN already exists)
 * 403: Forbidden (invalid auth token)
 * 500: Internal server error
 */
export const submitAbn = async (
  req: SubmitAbnFields
): Promise<SubmitAbnFields> => {
  try {
    const response = await axios.post(
      '/v1/australian_tax_info',
      snakeCaseObjectKeys(req),
      bffOpts
    )

    return camelCaseObjectKeys(response.data)
  } catch (error) {
    const errorBody = formatError(error)
    throw errorBody
  }
}

// ---------------------* GET: Has ABN *---------------------

export interface GetHasAbnResponse {
  hasAbn: boolean
}

/**
 * Get and check if ABN exists for a Dasher applicant
 * @returns A boolean indicating whether applicant has an ABN
 *
 * Success
 * 200 with response: ABN exists
 * 200 with empty response: ABN does not exist
 *
 * Error:
 * 400: Bad request (improper request format)
 * 403: Forbidden (invalid auth token)
 * 500: Internal server error
 */
export const getHasAbn = async (): Promise<GetHasAbnResponse> => {
  try {
    const response = await axios.get('/v1/australian_tax_info', {
      ...bffOpts,
    })

    return { hasAbn: !isEmpty(response.data) }
  } catch (error) {
    const errorBody = formatError(error)
    throw errorBody
  }
}

const formatError = error => {
  let errorBody
  if (error.response) {
    errorBody = { message: error.response.data }
  } else {
    errorBody = { message: error.message }
  }
  return errorBody
}
